import { calculatorSystem } from "./calculator-systems";
import { DIRECT_TERMINAL_MOUNT_NOTES } from "./connection-overview";
import { EXAMPLE_CHARGER_INPUT } from "./dc-dc-charger-calculator";
import { routeExample, viaX } from "./example-layout";
import { calculateLifepo4Fuse } from "./lifepo4-fuse-calculator";
import { EXAMPLE_WIRE_INPUT } from "./wire-size-calculator";
import type { Cable, ComponentNode, System } from "./types";

export type ToolExampleId = "wire-size" | "dc-dc-charger-size" | "lifepo4-fuse-size";

export const FUSE_EXAMPLE_INPUT = {
  maximumContinuousChargeCurrentA: 0,
  maximumContinuousDischargeCurrentA: 40,
};
const fuseBaseline = calculateLifepo4Fuse(FUSE_EXAMPLE_INPUT).sizing.continuousBaselineA!;

/** These are fixed worked examples, separate from a visitor's calculator inputs. */
export const TOOL_EXAMPLES = {
  "wire-size": {
    title: "12 V wire-size example with a 20 A load",
    label: "Wire size — 20 A load and equal return",
    lead: "A 12 V supply feeds a 20 A load through a terminal fuse, 5 m of positive cable and a separate 5 m return. The cable lengths and current match the worked calculation below.",
    points: [
      { title: "One way means 5 m", body: "The positive cable runs from the supply to the load. A separate cable returns to supply negative: 10 m of copper in the complete circuit." },
      { title: "Read both cable losses", body: "The drawing reports each conductor's voltage drop. Add the outgoing and return losses to compare with the calculator's complete-loop result." },
    ],
    assumptions: "Fixed metric example: 20 A at 12 V, 3% total drop allowance, 105°C copper outside engine spaces at up to 30°C, two conductors and no extra bundling. Wire area comes from the calculator.\n\nThe illustrative Blue Sea 5175 30 A MRBF fuse in a 5191 terminal holder is above the 25 A continuous-current baseline and below the modeled cable ampacity. The expanded zero-length connection represents a terminal mount, not extra cable.\n\nVerify the equipment's permitted fuse, whole-bank fault current, maximum voltage, holder installation and exact fuse before building. Fuse/contact resistance is outside the wire calculation; your calculator inputs do not select this fuse.",
    build: buildWireToolExample,
  },
  "dc-dc-charger-size": {
    title: "DC-DC charging example with 30 A output",
    label: "DC-DC sizing — vehicle supply, charger and auxiliary bank",
    lead: "The worked 30 A charging point draws 38.4 A from the vehicle at 12.5 V, using the example's 90% efficiency assumption and 14.4 V output.",
    points: [
      { title: "Vehicle side: 38.4 A", body: "The positive and negative input cables are each 3 m one way. The 480 W input includes the example's conversion loss." },
      { title: "Battery side: 30 A", body: "The positive and negative output cables are each 1 m one way. The charger supplies 432 W at the 14.4 V charging point." },
    ],
    assumptions: "Synthetic operating point from the worked calculator example, not measured vehicle or battery evidence. Voltages are operating values, not nominal bank ratings. Logical negative terminals are not a physical charger pinout.\n\nBlue Sea 5191 terminal holders carry illustrative 5177 50 A input and 5176 40 A output MRBF fuses: the next available ratings above 125% of 38.4 A and 30 A. These are planning choices, not charger-manufacturer recommendations. Each holder mounts at its battery's positive terminal; expanded zero-length connections are mounting markers, not cable.\n\nVerify actual charger limits, whole-bank fault current, maximum voltage, installation and control wiring. Recalculate after changing the operating point; your calculator inputs do not select these fuses.",
    build: buildChargerToolExample,
  },
  "lifepo4-fuse-size": {
    title: "LiFePO₄ fuse example with a 40 A load",
    label: "LiFePO₄ protection — battery, fuse and DC load",
    lead: `A 40 A continuous load gives a ${fuseBaseline} A preliminary baseline with the calculator's 125% headroom. The fuse sits in the positive path near the source; the negative cable completes the circuit.`,
    points: [
      { title: "The short unfused lead", body: "The example draws 0.1 m from battery positive to the fuse holder. That lead is upstream of the fuse. Check the permitted placement and protect the lead against a short." },
      { title: "The downstream load cable", body: `The remaining 2.9 m is downstream of the proposed ${fuseBaseline} A fuse; the negative return is 3 m. The exact fuse and holder still need to be verified.` },
    ],
    assumptions: `Illustrative 12 V circuit with 40 A discharge and no charging source. The ${fuseBaseline} A shown is only a continuous-current baseline, not a selected or verified fuse. Surge behavior, equipment limits, cable derating, exact fuse/holder DC ratings, whole-bank fault current, BMS and placement need evidence. The 0.1 m lead is an example dimension, not a universal permitted distance.`,
    build: buildFuseToolExample,
  },
} satisfies Record<ToolExampleId, {
  title: string; label: string; lead: string;
  points: Array<{ title: string; body: string }>;
  assumptions: string; build: () => System;
}>;

function finish(system: System, labels: Record<string, string> = {}): System {
  const pinned = {
    ...system,
    nodes: system.nodes.map((node) => node.kind !== "component" || !node.customDefinition ? node : {
      ...node,
      label: labels[node.id] ?? node.label,
      // Worked placeholders must not present generic caps as product ratings.
      customDefinition: { ...node.customDefinition, name: labels[node.id] ?? node.customDefinition.name,
        terminals: node.customDefinition.terminals.map(terminal => ({ ...terminal, maxAmp: 0 })) },
      terminalOverrides: Object.fromEntries(node.customDefinition.terminals.map((terminal) => [terminal.id, {
        side: terminal.side, offset: terminal.offset, pinned: true,
      }])),
    }),
    cables: system.cables.map((cable, index) => ({ ...cable, label: `C${index + 1}` })),
  };
  return routeExample(pinned, Object.fromEntries(pinned.cables.map((cable) => [cable.id,
    (from, to) => viaX((from.x + to.x) / 2)(from, to),
  ])));
}

function buildWireToolExample(): System {
  const system = calculatorSystem({ example: "wire-size", variant: "calculated", units: "metric",
    parameters: { version: 1, input: { ...EXAMPLE_WIRE_INPUT } } })!;
  const holder = terminalHolder("source-fuse", 300, 30, "5175");
  const [positive, negative] = system.cables;
  return finish(withProtectionNotes({ ...system,
    nodes: [...system.nodes.map(node => node.id === "load" ? { ...node, position: { x: 600, y: 100 } } : node), holder],
    cables: [terminalMount("source-mount", "source", holder.id),
      { ...positive, fromNodeId: holder.id, fromTerminalId: "out" }, negative],
  }, "wire-size"), { source: "12 V supply" });
}

function buildChargerToolExample(): System {
  const system = calculatorSystem({ example: "dc-dc-charger-size", variant: "calculated", units: "metric",
    parameters: { version: 1, input: { ...EXAMPLE_CHARGER_INPUT }, inputLengthM: 3, outputLengthM: 1 } })!;
  const inputHolder = terminalHolder("vehicle-fuse", 300, 50, "5177");
  const outputHolder = terminalHolder("house-fuse", 1000, 40, "5176", true);
  return finish(withProtectionNotes({ ...system,
    nodes: [...system.nodes.map(node => node.id === "charger" ? { ...node, position: { x: 600, y: 100 } }
      : node.id === "house" ? { ...node, position: { x: 1160, y: 100 } } : node), inputHolder, outputHolder],
    cables: [terminalMount("vehicle-mount", "vehicle", inputHolder.id),
      ...system.cables.map(cable => cable.id === "vehicle-feed" ? { ...cable, fromNodeId: inputHolder.id, fromTerminalId: "out" }
        : cable.id === "charge-feed" ? { ...cable, toNodeId: outputHolder.id, toTerminalId: "out" } : cable),
      terminalMount("house-mount", "house", outputHolder.id)],
  }, "dc-dc-charger-size"), { vehicle: "Vehicle supply", charger: "30 A DC-DC charger", house: "Auxiliary bank" });
}

/** Blue Sea 5191 mounts directly on a battery stud. The 30/40/50 A fuse
 * identities are illustrative choices supported by the exact product list,
 * not assumptions about an unnamed battery's fault current or a charger. */
function terminalHolder(id: string, x: number, ratingA: number, fusePart: string, reversed = false): ComponentNode {
  return { id, kind: "component", definitionId: "dist-holder-mrbf", position: { x, y: 110 }, fuseAmp: ratingA, rotation: reversed ? 180 : 0,
    notes: `Illustrative Blue Sea ${fusePart} ${ratingA} A MRBF fuse in a Blue Sea 5191 holder. Holder mounts directly on the battery positive stud. Verify the installed battery, fuse, maximum voltage, prospective fault current, terminal fit and torque. https://www.bluesea.com/products/${fusePart}/MRBF_Terminal_Fuse_-_${ratingA}A`,
  };
}

function terminalMount(id: string, sourceId: string, holderId: string): Cable {
  return { id, fromNodeId: sourceId, fromTerminalId: "pos", toNodeId: holderId, toTerminalId: "in", lengthM: 0,
    notes: `${DIRECT_TERMINAL_MOUNT_NOTES} Blue Sea 5191 mounting foot on the source's positive stud. The spacing is expanded for readability.` };
}

/** Retain calculator assumptions, but do not carry its unfused-draft wording
 * into the separate fixed example after adding illustrated source protection. */
function withProtectionNotes(system: System, id: "wire-size" | "dc-dc-charger-size"): System {
  const annotate = (notes = "") => `${notes.replace("Planning circuit. No fuse or holder is selected or physically placed.", "Fixed worked circuit with illustrative source-terminal protection.").replace("No fuse selected.", "Illustrative source-terminal protection is shown.")} ${TOOL_EXAMPLES[id].assumptions}`.trim();
  return { ...system, nodes: system.nodes.map(node => node.kind === "component" ? { ...node, notes: annotate(node.notes) } : node),
    cables: system.cables.map(cable => ({ ...cable, notes: annotate(cable.notes) })) };
}

function buildFuseToolExample(): System {
  const system = calculatorSystem({ example: "wire-size", variant: "calculated", units: "metric",
    parameters: { version: 1, input: { ...EXAMPLE_WIRE_INPUT, currentA: FUSE_EXAMPLE_INPUT.maximumContinuousDischargeCurrentA, oneWayLengthM: 3 } } })!;
  const source = system.nodes[0] as ComponentNode;
  source.label = "LiFePO₄ bank";
  source.customDefinition = { ...source.customDefinition!, name: source.label, chemistry: "lifepo4" };
  const load = system.nodes[1] as ComponentNode;
  load.position.x = 600;
  const notes = TOOL_EXAMPLES["lifepo4-fuse-size"].assumptions;
  source.notes = notes;
  load.notes = notes;
  const fuse: ComponentNode = {
    id: "fuse", kind: "component", definitionId: "custom", label: "Fuse + holder", position: { x: 320, y: 110 }, notes,
    customDefinition: {
      id: "tool-fuse-holder", name: "Fuse + holder", category: "distribution", isFuseBlock: true,
      renderVariant: "fuse-holder", width: 100, height: 40,
      terminals: [
        { id: "in", label: "IN", polarity: "positive", direction: "input", side: "left", offset: 0.5, maxAmp: 0 },
        { id: "out", label: "OUT", polarity: "positive", direction: "output", side: "right", offset: 0.5, maxAmp: 0 },
      ],
    },
  };
  const [positive, negative] = system.cables;
  return finish({ ...system, name: "LiFePO₄ fuse example — 40 A load", nodes: [source, fuse, load], cables: [
    { ...positive, id: "battery-lead", toNodeId: "fuse", toTerminalId: "in", lengthM: 0.1, notes },
    { ...positive, id: "protected-feed", fromNodeId: "fuse", fromTerminalId: "out", lengthM: 2.9, fuseAmpOverride: fuseBaseline, notes },
    { ...negative, notes },
  ] });
}
